import React, { useState, useEffect } from 'react';
import {
  Plus, X, Trash2, Edit3, Phone, ChevronDown, ChevronUp, User,
  Calendar, Clock, Check, XCircle, Bell, AlertCircle
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';

const emptyLead = {
  customerName: '',
  temperature: 'Hot',
  phone: '',
  paymentTerms: 'Financing',
  downPayment: 0,
  vehicleOfInterest: '',
  notes: '',
};

const tempColors = {
  Hot:  { bg: 'rgba(255,77,77,.12)', border: 'rgba(255,77,77,.35)', text: '#ff4d4d', emoji: '🔥' },
  Warm: { bg: 'rgba(255,200,0,.12)', border: 'rgba(255,200,0,.35)', text: '#ffc800', emoji: '☀️' },
  Cold: { bg: 'rgba(0,204,255,.12)', border: 'rgba(0,204,255,.35)', text: '#00ccff', emoji: '❄️' },
};

export default function Leads() {
  const {
    leads, addLead, updateLead, deleteLead,
    appointments, addAppointment, completeAppointment, noShowAppointment, deleteAppointment,
    upcomingAppointments,
    collapsedSections, toggleSection,
  } = useAppContext();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyLead });
  const [expandedId, setExpandedId] = useState(null);
  const [filterTemp, setFilterTemp] = useState('All');

  // Appointment form state
  const [showApptForm, setShowApptForm] = useState(false);
  const [apptLeadId, setApptLeadId] = useState(null);
  const [apptForm, setApptForm] = useState({ date: '', time: '', notes: '' });

  // Appointment scheduling in lead form
  const [scheduleAppt, setScheduleAppt] = useState(false);
  const [leadApptDate, setLeadApptDate] = useState('');
  const [leadApptTime, setLeadApptTime] = useState('');

  // View mode: 'leads' or 'appointments'
  const [viewMode, setViewMode] = useState('leads');

  // Listen for "add lead" event from Dashboard +Customer button
  useEffect(() => {
    const handler = (e) => {
      setEditingId(null);
      const prefill = e.detail?.customerName || '';
      setForm({ ...emptyLead, customerName: prefill });
      setScheduleAppt(false);
      setLeadApptDate('');
      setLeadApptTime('');
      setShowForm(true);
    };
    window.addEventListener('tracksy-add-lead', handler);
    return () => window.removeEventListener('tracksy-add-lead', handler);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) return;

    if (editingId) {
      updateLead(editingId, {
        ...form,
        downPayment: Number(form.downPayment) || 0,
      });
    } else {
      const newLead = addLead({
        ...form,
        downPayment: Number(form.downPayment) || 0,
      });

      // If scheduling appointment with the lead
      if (scheduleAppt && leadApptDate && leadApptTime) {
        try {
          const [y, m, d] = (leadApptDate || '').split('-').map(Number);
          const [h, min] = (leadApptTime || '').split(':').map(Number);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d) && !isNaN(h) && !isNaN(min)) {
            const apptDateTime = new Date(y, m - 1, d, h, min);
            if (!isNaN(apptDateTime.getTime())) {
              addAppointment({
                leadId: newLead.id,
                customerName: form.customerName.trim(),
                phone: form.phone || '',
                vehicleOfInterest: form.vehicleOfInterest || '',
                appointmentDate: apptDateTime.toISOString(),
                notes: '',
              });
            }
          }
        } catch (err) {
          console.error('Appointment scheduling error:', err);
        }
      }
    }
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyLead });
    setScheduleAppt(false);
    setLeadApptDate('');
    setLeadApptTime('');
  };

  const handleEdit = (lead) => {
    setForm({
      customerName: lead.customerName,
      temperature: lead.temperature,
      phone: lead.phone,
      paymentTerms: lead.paymentTerms,
      downPayment: lead.downPayment,
      vehicleOfInterest: lead.vehicleOfInterest,
      notes: lead.notes,
    });
    setEditingId(lead.id);
    setScheduleAppt(false);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this lead and its appointments?')) {
      deleteLead(id);
      if (expandedId === id) setExpandedId(null);
    }
  };

  // Schedule appointment for existing lead
  const handleScheduleAppt = (lead) => {
    setApptLeadId(lead.id);
    setApptForm({ date: '', time: '', notes: '' });
    setShowApptForm(true);
  };

  const handleApptSubmit = (e) => {
    e.preventDefault();
    try {
      if (!apptForm.date || !apptForm.time) return;
      const lead = leads.find(l => l.id === apptLeadId);
      if (!lead) return;

      const [y, m, d] = (apptForm.date || '').split('-').map(Number);
      const [h, min] = (apptForm.time || '').split(':').map(Number);
      
      // Validate parsed values
      if (isNaN(y) || isNaN(m) || isNaN(d) || isNaN(h) || isNaN(min)) {
        alert('Please enter a valid date and time.');
        return;
      }
      
      const apptDateTime = new Date(y, m - 1, d, h, min);
      if (isNaN(apptDateTime.getTime())) {
        alert('Invalid date/time. Please try again.');
        return;
      }

      addAppointment({
        leadId: lead.id,
        customerName: lead.customerName,
        phone: lead.phone || '',
        vehicleOfInterest: lead.vehicleOfInterest || '',
        appointmentDate: apptDateTime.toISOString(),
        notes: apptForm.notes || '',
      });
      setShowApptForm(false);
      setApptLeadId(null);
    } catch (err) {
      console.error('Appointment error:', err);
      alert('Something went wrong. Please try again.');
      setShowApptForm(false);
      setApptLeadId(null);
    }
  };

  const filtered = filterTemp === 'All' ? leads : leads.filter(l => l.temperature === filterTemp);
  const sorted = [...filtered].sort((a, b) => {
    const order = { Hot: 0, Warm: 1, Cold: 2 };
    return (order[a.temperature] ?? 3) - (order[b.temperature] ?? 3);
  });

  const hotCount  = leads.filter(l => l.temperature === 'Hot').length;
  const warmCount = leads.filter(l => l.temperature === 'Warm').length;
  const coldCount = leads.filter(l => l.temperature === 'Cold').length;

  // Get appointments for a specific lead
  const getLeadAppointments = (leadId) =>
    appointments.filter(a => a.leadId === leadId && a.status === 'upcoming')
      .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));

  // Completed / No-show appointments (recent)
  const completedAppts = appointments
    .filter(a => a.status === 'completed' || a.status === 'no-show')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    .slice(0, 10);

  const formatApptTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Unknown time';
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      if (isToday) return `Today at ${time}`;
      return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;
    } catch (e) {
      return 'Unknown time';
    }
  };

  // Get today's date string for min date on input
  const todayDateStr = new Date().toISOString().split('T')[0];

  return (
    <div className="view">
      <div className="leads-header">
        <h1 className="view-title">🎯 Leads</h1>
        <button className="btn-add-sale" onClick={() => {
          setEditingId(null);
          setForm({ ...emptyLead });
          setScheduleAppt(false);
          setLeadApptDate('');
          setLeadApptTime('');
          setShowForm(true);
        }}>
          <Plus size={18} /> Add Lead
        </button>
      </div>

      {/* Stats row */}
      <div className="leads-stats">
        <div className="lead-stat-card" style={{ background: 'rgba(255,77,77,.08)', borderColor: 'rgba(255,77,77,.25)' }}>
          <span className="lead-stat-emoji">🔥</span>
          <span className="lead-stat-num" style={{ color: '#ff4d4d' }}>{hotCount}</span>
          <span className="lead-stat-lbl">Hot</span>
        </div>
        <div className="lead-stat-card" style={{ background: 'rgba(255,200,0,.08)', borderColor: 'rgba(255,200,0,.25)' }}>
          <span className="lead-stat-emoji">☀️</span>
          <span className="lead-stat-num" style={{ color: '#ffc800' }}>{warmCount}</span>
          <span className="lead-stat-lbl">Warm</span>
        </div>
        <div className="lead-stat-card" style={{ background: 'rgba(0,204,255,.08)', borderColor: 'rgba(0,204,255,.25)' }}>
          <span className="lead-stat-emoji">❄️</span>
          <span className="lead-stat-num" style={{ color: '#00ccff' }}>{coldCount}</span>
          <span className="lead-stat-lbl">Cold</span>
        </div>
      </div>

      {/* View mode toggle: Leads vs Appointments */}
      <div className="leads-view-toggle">
        <button className={`view-toggle-btn ${viewMode === 'leads' ? 'view-toggle-active' : ''}`}
          onClick={() => setViewMode('leads')}>
          <User size={15} /> Leads ({leads.length})
        </button>
        <button className={`view-toggle-btn ${viewMode === 'appointments' ? 'view-toggle-active' : ''}`}
          onClick={() => setViewMode('appointments')}>
          <Calendar size={15} /> Appointments ({upcomingAppointments.length})
        </button>
      </div>

      {/* ══════════════ LEADS VIEW ══════════════ */}
      {viewMode === 'leads' && (
        <>
          {/* Filter pills */}
          <div className="leads-filter-row">
            {['All', 'Hot', 'Warm', 'Cold'].map(t => (
              <button key={t}
                className={`lead-filter-pill ${filterTemp === t ? 'lead-filter-active' : ''}`}
                style={filterTemp === t && t !== 'All' ? { background: tempColors[t]?.bg, borderColor: tempColors[t]?.border, color: tempColors[t]?.text } : {}}
                onClick={() => setFilterTemp(t)}>
                {t !== 'All' && tempColors[t]?.emoji} {t} {t === 'All' ? `(${leads.length})` : ''}
              </button>
            ))}
          </div>

          {/* Leads list */}
          <div className="section-card">
            <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('leads-list')}>
              <h2>All Leads ({sorted.length})</h2>
              <span className={`section-chevron ${collapsedSections['leads-list'] ? 'chevron-collapsed' : ''}`}>▼</span>
            </div>
            <div className={`section-collapse-content ${collapsedSections['leads-list'] ? 'section-collapsed' : ''}`}>
            {sorted.length === 0 && (
              <div className="empty-state">
                {leads.length === 0 ? 'No leads yet. Add your first lead! 🎯' : 'No leads match this filter.'}
              </div>
            )}
            <div className="leads-list">
              {sorted.map(lead => {
                const tc = tempColors[lead.temperature] || tempColors.Warm;
                const isExpanded = expandedId === lead.id;
                const leadAppts = getLeadAppointments(lead.id);
                return (
                  <div key={lead.id} className="lead-card">
                    <div className="lead-card-top" onClick={() => setExpandedId(isExpanded ? null : lead.id)}>
                      <div className="lead-card-left">
                        <div className="lead-avatar" style={{ background: tc.bg, borderColor: tc.border }}>
                          <User size={18} style={{ color: tc.text }} />
                        </div>
                        <div className="lead-card-info">
                          <div className="lead-card-name">
                            {lead.customerName}
                            {leadAppts.length > 0 && (
                              <span className="lead-appt-indicator" title={`${leadAppts.length} upcoming appointment(s)`}>
                                <Calendar size={12} /> {leadAppts.length}
                              </span>
                            )}
                          </div>
                          <div className="lead-card-meta">
                            <span className="lead-temp-badge" style={{ background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text }}>
                              {tc.emoji} {lead.temperature}
                            </span>
                            {lead.vehicleOfInterest && <span className="lead-vehicle-tag">{lead.vehicleOfInterest}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="lead-card-right">
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="lead-card-details">
                        {lead.phone && (
                          <div className="lead-detail-row">
                            <Phone size={14} style={{ color: '#00ff99', flexShrink: 0 }} />
                            <a href={`tel:${lead.phone}`} className="lead-phone-link">{lead.phone}</a>
                          </div>
                        )}
                        <div className="lead-detail-grid">
                          <div className="lead-detail-item">
                            <span className="lead-detail-lbl">Payment</span>
                            <span className="lead-detail-val">{lead.paymentTerms}</span>
                          </div>
                          <div className="lead-detail-item">
                            <span className="lead-detail-lbl">Down Payment</span>
                            <span className="lead-detail-val">${(lead.downPayment || 0).toLocaleString()}</span>
                          </div>
                          <div className="lead-detail-item">
                            <span className="lead-detail-lbl">Vehicle</span>
                            <span className="lead-detail-val">{lead.vehicleOfInterest || '—'}</span>
                          </div>
                          <div className="lead-detail-item">
                            <span className="lead-detail-lbl">Added</span>
                            <span className="lead-detail-val">{new Date(lead.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {lead.notes && (
                          <div className="lead-notes-box">
                            <span className="lead-detail-lbl">Notes</span>
                            <p className="lead-notes-text">{lead.notes}</p>
                          </div>
                        )}

                        {/* Lead's upcoming appointments */}
                        {leadAppts.length > 0 && (
                          <div className="lead-appts-section">
                            <span className="lead-detail-lbl">📅 Upcoming Appointments</span>
                            {leadAppts.map(appt => (
                              <div key={appt.id} className="lead-appt-row">
                                <div className="lead-appt-info">
                                  <Clock size={13} style={{color:'#a855f7'}} />
                                  <span>{formatApptTime(appt.appointmentDate)}</span>
                                  {appt.notes && <span className="lead-appt-note">— {appt.notes}</span>}
                                </div>
                                <div className="lead-appt-actions">
                                  <button className="lead-appt-btn lead-appt-complete" onClick={(e) => { e.stopPropagation(); completeAppointment(appt.id); }} title="Completed">
                                    <Check size={13} />
                                  </button>
                                  <button className="lead-appt-btn lead-appt-noshow" onClick={(e) => { e.stopPropagation(); noShowAppointment(appt.id); }} title="No-Show">
                                    <XCircle size={13} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="lead-card-actions">
                          <button className="lead-action-btn lead-appt-schedule-btn" onClick={(e) => { e.stopPropagation(); handleScheduleAppt(lead); }}>
                            <Calendar size={14} /> Schedule Appt
                          </button>
                          <button className="lead-action-btn lead-edit-btn" onClick={(e) => { e.stopPropagation(); handleEdit(lead); }}>
                            <Edit3 size={14} /> Edit
                          </button>
                          <button className="lead-action-btn lead-delete-btn" onClick={(e) => { e.stopPropagation(); handleDelete(lead.id); }}>
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>{/* end section-collapse-content */}
          </div>
        </>
      )}

      {/* ══════════════ APPOINTMENTS VIEW ══════════════ */}
      {viewMode === 'appointments' && (
        <>
          {/* Upcoming */}
          <div className="section-card">
            <div className="section-hdr">
              <h2><Bell size={16} /> Upcoming</h2>
              <span className="appt-count-badge">{upcomingAppointments.length}</span>
            </div>
            {upcomingAppointments.length === 0 && (
              <div className="empty-state">No upcoming appointments. Schedule one from a lead! 📅</div>
            )}
            <div className="appt-full-list">
              {upcomingAppointments.map(appt => {
                const apptDate = new Date(appt.appointmentDate);
                const isPast = apptDate < new Date();
                const isSoon = !isPast && (apptDate - new Date()) < 3600000;
                return (
                  <div key={appt.id} className={`appt-full-card ${isPast ? 'appt-overdue' : ''} ${isSoon ? 'appt-soon' : ''}`}>
                    <div className="appt-full-top">
                      <div className="appt-full-left">
                        <div className={`appt-status-dot ${isPast ? 'dot-overdue' : isSoon ? 'dot-soon' : 'dot-upcoming'}`} />
                        <div>
                          <div className="appt-full-name">{appt.customerName}</div>
                          <div className="appt-full-time">
                            <Clock size={13} /> {formatApptTime(appt.appointmentDate)}
                          </div>
                          {appt.vehicleOfInterest && <div className="appt-full-vehicle">🚗 {appt.vehicleOfInterest}</div>}
                          {appt.phone && <div className="appt-full-phone"><Phone size={12} /> {appt.phone}</div>}
                          {appt.notes && <div className="appt-full-notes">{appt.notes}</div>}
                        </div>
                      </div>
                      {isPast && (
                        <div className="appt-overdue-badge">
                          <AlertCircle size={13} /> Overdue
                        </div>
                      )}
                    </div>
                    <div className="appt-full-actions">
                      <button className="appt-full-btn appt-full-complete" onClick={() => completeAppointment(appt.id)}>
                        <Check size={15} /> Completed
                      </button>
                      <button className="appt-full-btn appt-full-noshow" onClick={() => noShowAppointment(appt.id)}>
                        <XCircle size={15} /> No-Show
                      </button>
                      <button className="appt-full-btn appt-full-delete" onClick={() => {
                        if (window.confirm('Delete this appointment?')) deleteAppointment(appt.id);
                      }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Completed / No-Show History */}
          {completedAppts.length > 0 && (
            <div className="section-card">
              <div className="section-hdr"><h2>📋 Recent History</h2></div>
              <div className="appt-history-list">
                {completedAppts.map(appt => (
                  <div key={appt.id} className={`appt-history-row ${appt.status}`}>
                    <div className={`appt-history-status ${appt.status === 'completed' ? 'history-completed' : 'history-noshow'}`}>
                      {appt.status === 'completed' ? <Check size={14} /> : <XCircle size={14} />}
                    </div>
                    <div className="appt-history-info">
                      <div className="appt-history-name">{appt.customerName}</div>
                      <div className="appt-history-meta">
                        {formatApptTime(appt.appointmentDate)}
                        <span className={`appt-history-badge ${appt.status}`}>
                          {appt.status === 'completed' ? '✅ Completed' : '❌ No-Show'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════ ADD / EDIT LEAD MODAL ══════════════ */}
      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingId(null); }}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{editingId ? 'Edit Lead' : 'Add Lead'}</h3>
              <button className="modal-x" onClick={() => { setShowForm(false); setEditingId(null); }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Customer Name *</label>
                <input type="text" required value={form.customerName}
                  onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                  placeholder="John Smith" autoFocus />
              </div>
              <div className="form-group">
                <label>Lead Temperature</label>
                <select value={form.temperature} onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))}>
                  <option value="Hot">🔥 Hot</option>
                  <option value="Warm">☀️ Warm</option>
                  <option value="Cold">❄️ Cold</option>
                </select>
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="(555) 123-4567" />
              </div>
              <div className="form-group">
                <label>Payment Terms</label>
                <select value={form.paymentTerms} onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))}>
                  <option value="Financing">Financing</option>
                  <option value="Cash">Cash</option>
                  <option value="Lease">Lease</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Down Payment ($)</label>
                <input type="number" value={form.downPayment}
                  onFocus={e => { if (e.target.value === '0') e.target.select(); }}
                  onChange={e => setForm(f => ({ ...f, downPayment: e.target.value === '' ? '' : e.target.value }))}
                  onBlur={e => { if (e.target.value === '') setForm(f => ({ ...f, downPayment: 0 })); }}
                  placeholder="0" />
              </div>
              <div className="form-group">
                <label>Vehicle of Interest</label>
                <input type="text" value={form.vehicleOfInterest}
                  onChange={e => setForm(f => ({ ...f, vehicleOfInterest: e.target.value }))}
                  placeholder="e.g. 2024 Toyota Camry" />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Customer details, preferences, follow-up notes..."
                  rows="3"
                  style={{width:'100%',background:'rgba(255,255,255,.07)',border:'1px solid rgba(255,255,255,.12)',color:'#fff',padding:'12px',borderRadius:'11px',fontSize:'14px',fontFamily:'inherit',resize:'vertical'}}
                />
              </div>

              {/* Schedule Appointment (only for new leads) */}
              {!editingId && (
                <div className="form-appt-section">
                  <label className="form-appt-toggle" onClick={() => setScheduleAppt(!scheduleAppt)}>
                    <div className={`toggle-switch ${scheduleAppt ? 'toggle-on' : ''}`}>
                      <div className="toggle-knob" />
                    </div>
                    <span>📅 Schedule Appointment</span>
                  </label>
                  {scheduleAppt && (
                    <div className="form-appt-fields">
                      <div className="form-row-2">
                        <div className="form-group">
                          <label>Date *</label>
                          <input type="date" value={leadApptDate} min={todayDateStr}
                            onChange={e => setLeadApptDate(e.target.value)} required={scheduleAppt} />
                        </div>
                        <div className="form-group">
                          <label>Time *</label>
                          <input type="time" value={leadApptTime}
                            onChange={e => setLeadApptTime(e.target.value)} required={scheduleAppt} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="btn-primary">
                {editingId ? 'Save Changes' : scheduleAppt ? 'Add Lead + Schedule' : 'Add Lead'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════ SCHEDULE APPOINTMENT MODAL ══════════════ */}
      {showApptForm && (
        <div className="modal-overlay" onClick={() => setShowApptForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>📅 Schedule Appointment</h3>
              <button className="modal-x" onClick={() => setShowApptForm(false)}><X size={20} /></button>
            </div>
            <div className="appt-modal-customer">
              {leads.find(l => l.id === apptLeadId)?.customerName}
            </div>
            <form onSubmit={handleApptSubmit} className="modal-form">
              <div className="form-row-2">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={apptForm.date} min={todayDateStr}
                    onChange={e => setApptForm(f => ({ ...f, date: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Time *</label>
                  <input type="time" value={apptForm.time}
                    onChange={e => setApptForm(f => ({ ...f, time: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <input type="text" value={apptForm.notes}
                  onChange={e => setApptForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Test drive, financing discussion..." />
              </div>
              <button type="submit" className="btn-primary">
                <Calendar size={16} /> Schedule Appointment
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
